import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, Star, GripVertical, List, WifiOff, Link2, Unlink2 } from 'lucide-react';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import api from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';

const METHODS   = ['법인카드','세금계산서','현금영수증','영수증','개인카드'];
const RECEIPTS  = ['수령','미수령','-'];
const EXECUTORS = ['김태연','이정찬','김상경'];
const COLORS    = ['#a78bfa','#f87171','#fb923c','#facc15','#4ade80','#22d3ee','#818cf8','#e879f9'];

const fmt = n => (n || 0).toLocaleString('ko-KR');
const emptyItem = () => ({
  itemName:'', itemCategory:'', description:'',
  unitPrice:0, quantity:1, unit:1,
  budgetAmount:0, spentAmount:0,
  executor:'', spentDate:'', paymentMethod:'', receiptYn:'-', note:'',
  isImportant:false,
});

export default function EventBudgetTab({ eventId }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const me = JSON.parse(localStorage.getItem('paper_work_user') || '{}');
  const token = localStorage.getItem('paper_work_token');
  const isSuperAdmin = me?.role === 'superadmin';

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const [newCatName, setNewCatName] = useState('');
  const [showNewCat, setShowNewCat] = useState(false);
  const [focusedCell, setFocusedCell] = useState(null);
  const [dragItem, setDragItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [expandedSpending, setExpandedSpending] = useState(null);

  // ─── Yjs ─────────────────────────────────────────────
  const [connected, setConnected] = useState(false);
  const [peers, setPeers] = useState([]);
  const [peerCells, setPeerCells] = useState({});
  const versionRef = useRef(0);
  const peersRef = useRef('[]');
  const cellsRef = useRef('{}');

  const wsUrl = useMemo(() => {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/yjs`;
  }, []);

  const { ydoc, provider } = useMemo(() => {
    const doc = new Y.Doc();
    const prov = new WebsocketProvider(wsUrl, `event-budget-${eventId}`, doc, { params: { token } });
    return { ydoc: doc, provider: prov };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get(`/event-manage/projects/${eventId}/budget`);
      setCategories(data);
    } catch {}
    setLoading(false);
  }, [eventId]);

  useEffect(() => {
    provider.on('status', ({ status }) => setConnected(status === 'connected'));
    if (provider.wsconnected) setConnected(true);
    provider.awareness.setLocalStateField('user', {
      name: me?.name || me?.username || 'Unknown',
      color: COLORS[(me?.id || 0) % COLORS.length],
      profileImage: me?.avatarUrl || null,
    });
    const ymap = ydoc.getMap('sync');
    const onMapChange = () => {
      const v = ymap.get('version') || 0;
      if (v > versionRef.current) { versionRef.current = v; fetchData(); }
    };
    ymap.observe(onMapChange);
    const onAwareness = () => {
      const myName = me?.name || me?.username || 'Unknown';
      const seen = new Set(); const users = []; const cells = {};
      provider.awareness.getStates().forEach(state => {
        if (!state.user) return;
        if (!seen.has(state.user.name)) { seen.add(state.user.name); users.push({ ...state.user, isMe: state.user.name === myName }); }
        if (state.user.editingCell && state.user.name !== myName) cells[state.user.editingCell] = { name: state.user.name, color: state.user.color };
      });
      const idx = users.findIndex(u => u.isMe);
      if (idx > 0) users.unshift(users.splice(idx, 1)[0]);
      const pStr = JSON.stringify(users); const cStr = JSON.stringify(cells);
      if (pStr !== peersRef.current) { peersRef.current = pStr; setPeers(users); }
      if (cStr !== cellsRef.current) { cellsRef.current = cStr; setPeerCells(cells); }
    };
    provider.awareness.on('change', onAwareness);
    onAwareness();
    return () => { provider.destroy(); ydoc.destroy(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, ydoc]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const notifyChange = () => {
    const ymap = ydoc.getMap('sync');
    versionRef.current = (ymap.get('version') || 0) + 1;
    ymap.set('version', versionRef.current);
  };
  const setEditingCell = (cellId) => {
    provider.awareness.setLocalStateField('user', {
      ...provider.awareness.getLocalState()?.user,
      editingCell: cellId,
    });
  };

  // ─── CRUD ─────────────────────────────────────────────
  const addCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      const { data } = await api.post(`/event-manage/projects/${eventId}/budget/categories`, { name: newCatName });
      setCategories(p => [...p, data]);
      setNewCatName(''); setShowNewCat(false); notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };
  const deleteCategory = async (catId) => {
    if (!window.confirm('대분류를 삭제하면 모든 항목도 삭제됩니다. 계속하시겠습니까?')) return;
    try {
      await api.delete(`/event-manage/budget/categories/${catId}`);
      setCategories(p => p.filter(c => c.id !== catId)); notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };
  const updateCategoryName = async (catId, name) => {
    try { await api.put(`/event-manage/budget/categories/${catId}`, { name }); notifyChange(); }
    catch { alert('오류가 발생했습니다.'); }
  };
  // 위 행과 합치기 / 분리.
  // 분리하면 splitFromAbove가 켜져 이름과 무관하게 새 그룹이 되고,
  // 그 행에 항목/구분 입력칸이 다시 생긴다.
  // 합치면 플래그를 끄고 이름을 비워 위 그룹으로 흡수시킨다.
  const toggleSplit = async (catId, item, split) => {
    const patch = split ? { splitFromAbove: true } : { splitFromAbove: false, itemName: '', itemCategory: '' };
    setCategories(p => p.map(c => c.id !== catId ? c : {
      ...c, items: c.items.map(it => it.id === item.id ? { ...it, ...patch } : it),
    }));
    try {
      await api.put(`/event-manage/budget/items/${item.id}`, patch);
      notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };

  const addItem = async (catId) => {
    try {
      const cat = categories.find(c => c.id === catId);
      const { data } = await api.post(`/event-manage/budget/categories/${catId}/items`, { ...emptyItem(), order: (cat?.items || []).length });
      setCategories(p => p.map(c => c.id === catId ? { ...c, items: [...(c.items || []), data] } : c));
      notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };
  const updateItemLocal = (catId, itemId, field, value) => {
    setCategories(p => p.map(c => c.id !== catId ? c : {
      ...c, items: c.items.map(it => {
        if (it.id !== itemId) return it;
        const u = { ...it, [field]: value };
        if (['unitPrice','quantity','unit'].includes(field)) {
          const p2 = field === 'unitPrice' ? value : (it.unitPrice || 0);
          const q  = field === 'quantity'  ? value : (it.quantity  || 1);
          const u2 = field === 'unit'      ? value : (Number(it.unit) || 1);
          u.budgetAmount = p2 * q * u2;
        }
        return u;
      })
    }));
  };
  const saveItem = async (item) => {
    const { order, id, eventBudgetCategoryId, createdAt, updatedAt, spendings, ...fields } = item;
    try { await api.put(`/event-manage/budget/items/${item.id}`, fields); notifyChange(); }
    catch { alert('저장 중 오류가 발생했습니다.'); }
  };
  const deleteItem = async (catId, itemId) => {
    try {
      await api.delete(`/event-manage/budget/items/${itemId}`);
      setCategories(p => p.map(c => c.id !== catId ? c : { ...c, items: c.items.filter(it => it.id !== itemId) }));
      notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };

  // 세부지출
  const addSpending = async (catId, itemId) => {
    try {
      const { data } = await api.post(`/event-manage/budget/items/${itemId}/spendings`, { amount: 0 });
      setCategories(p => p.map(c => c.id !== catId ? c : {
        ...c, items: c.items.map(it => it.id !== itemId ? it : { ...it, spendings: [...(it.spendings || []), data] })
      }));
      notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };
  const updateSpendingLocal = (catId, itemId, spId, field, value) => {
    setCategories(p => p.map(c => c.id !== catId ? c : {
      ...c, items: c.items.map(it => it.id !== itemId ? it : {
        ...it, spendings: (it.spendings || []).map(sp => sp.id !== spId ? sp : { ...sp, [field]: value })
      })
    }));
  };
  const saveSpending = async (sp) => {
    const { id, eventBudgetItemId, createdAt, updatedAt, ...fields } = sp;
    try { await api.put(`/event-manage/budget/spendings/${sp.id}`, fields); notifyChange(); }
    catch { alert('저장 중 오류가 발생했습니다.'); }
  };
  const deleteSpending = async (catId, itemId, spId) => {
    try {
      await api.delete(`/event-manage/budget/spendings/${spId}`);
      setCategories(p => p.map(c => c.id !== catId ? c : {
        ...c, items: c.items.map(it => it.id !== itemId ? it : {
          ...it, spendings: (it.spendings || []).filter(sp => sp.id !== spId)
        })
      }));
      notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };

  // 드래그
  const onDragStart = (catId, itemId) => setDragItem({ catId, itemId });
  const onDragOver  = (e, catId, itemId) => { e.preventDefault(); setDragOverItem({ catId, itemId }); };
  const onDragEnd   = () => { setDragItem(null); setDragOverItem(null); };
  const onDrop = (catId, targetId) => {
    if (!dragItem || dragItem.catId !== catId || dragItem.itemId === targetId) return;
    const cat = categories.find(c => c.id === catId);
    if (!cat) return;
    const arr = [...cat.items];
    const from = arr.findIndex(it => it.id === dragItem.itemId);
    const to   = arr.findIndex(it => it.id === targetId);
    if (from === -1 || to === -1) return;
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setCategories(p => p.map(c => c.id !== catId ? c : { ...c, items: arr.map((it, i) => ({ ...it, order: i })) }));
    api.put('/event-manage/budget/reorder-items', arr.map((it, i) => ({ id: it.id, order: i }))).then(() => notifyChange());
    setDragItem(null); setDragOverItem(null);
  };

  const getSpent = it => {
    const sps = it.spendings || [];
    return sps.length > 0 ? sps.reduce((s, sp) => s + (Number(sp.amount) || 0), 0) : (Number(it.spentAmount) || 0);
  };

  // rowspan
  // splitFromAbove가 켜진 행은 이름과 무관하게 항상 새 그룹을 시작한다.
  // (이게 없으면 한 번 합쳐진 행은 항목 셀 자체가 렌더링되지 않아
  //  이름을 다시 입력할 방법이 없어 되돌릴 수 없었다.)
  const getLogicalRanges = (items, getKey) => {
    const ranges = []; let start = 0;
    while (start < items.length) {
      const name = getKey(items[start]);
      if (name === '') { ranges.push([start, start + 1]); start++; continue; }
      let end = start + 1;
      while (end < items.length) {
        if (items[end].splitFromAbove) break;
        const next = getKey(items[end]);
        if (next === '' || next === name) end++; else break;
      }
      ranges.push([start, end]); start = end;
    }
    return ranges;
  };
  const calcSpans = (items, getKey, ranges) => {
    const spans = Array(items.length).fill(1);
    const segs = ranges || [[0, items.length]];
    for (const [start, end] of segs) {
      let i = start;
      while (i < end) {
        const key = getKey(items[i]);
        let j = i + 1;
        while (j < end && getKey(items[j]) === key && key !== '') j++;
        if (j > i + 1) { spans[i] = j - i; for (let k = i + 1; k < j; k++) spans[k] = 0; }
        i = j;
      }
    }
    return spans;
  };
  const getRowSpans = (items) => {
    const nameRanges = getLogicalRanges(items, it => it.itemName || '');
    const nameSpans = Array(items.length).fill(1);
    for (const [s, e] of nameRanges) {
      if (e - s > 1) { nameSpans[s] = e - s; for (let k = s + 1; k < e; k++) nameSpans[k] = 0; }
    }
    const catSpans = calcSpans(items, it => it.itemCategory || '', nameRanges);
    return { nameSpans, catSpans };
  };

  const grandBudget = categories.reduce((s, c) => s + (c.items || []).reduce((ss, it) => ss + (Number(it.budgetAmount) || 0), 0), 0);
  const grandSpent  = categories.reduce((s, c) => s + (c.items || []).reduce((ss, it) => ss + getSpent(it), 0), 0);

  // 테마 클래스
  const bdr = isDark ? 'border-white/10' : 'border-gray-200';
  // budget-table: 셀 안의 숫자 증감 화살표 / 셀렉트 화살표 / 달력 아이콘을
  // 전부 숨겨 좁은 셀에서 입력값이 가려지지 않게 한다 (index.css)
  const cellCls = `border ${bdr} px-2 py-1.5 text-xs`;
  const hCls = `${cellCls} whitespace-nowrap text-center font-medium ${isDark ? 'bg-white/3 text-white/40' : 'bg-gray-50 text-gray-500'}`;
  const inputCls = `w-full bg-transparent ${isDark ? 'text-white' : 'text-gray-900'} text-xs outline-none ${isDark ? 'focus:bg-white/5' : 'focus:bg-gray-100'} rounded px-1 py-0.5`;
  const numInputCls = `${inputCls} text-right`;
  const centerInputCls = `${inputCls} text-center`;
  const centerSelectCls = `w-full bg-transparent ${isDark ? 'text-white' : 'text-gray-900'} text-xs outline-none rounded px-1 py-0.5 cursor-pointer text-center`;
  const optBg = isDark ? '#1a1428' : '#fff';

  if (loading) return <div className={`py-20 text-center text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>로딩 중...</div>;

  return (
    <div>
      {/* 상단 합계 */}
      <div className="flex items-center gap-6 mb-5">
        <div className="text-sm">
          <span className={isDark ? 'text-white/40' : 'text-gray-400'}>총 예산</span>
          <span className={`font-semibold ml-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{fmt(grandBudget)}원</span>
        </div>
        <div className="text-sm">
          <span className={isDark ? 'text-white/40' : 'text-gray-400'}>총 지출</span>
          <span className="text-amber-500 font-semibold ml-2">{fmt(grandSpent)}원</span>
        </div>
        <div className="text-sm">
          <span className={isDark ? 'text-white/40' : 'text-gray-400'}>잔여</span>
          <span className={`font-semibold ml-2 ${grandBudget - grandSpent < 0 ? 'text-red-400' : isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{fmt(grandBudget - grandSpent)}원</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {peers.slice(0, 5).map((p, i) => (
            <div key={i} className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white overflow-hidden border-2 ${p.isMe ? 'border-brand' : 'border-transparent'}`}
              style={{ backgroundColor: p.color }} title={p.name}>
              {p.profileImage ? <img src={p.profileImage} alt="" className="w-full h-full object-cover" /> : p.name?.charAt(0)}
            </div>
          ))}
          {!connected && <WifiOff size={13} className="text-red-400" />}
        </div>
      </div>

      <div className="space-y-5">
        {categories.map(cat => {
          const items = cat.items || [];
          const totalBudget = items.reduce((s, it) => s + (Number(it.budgetAmount) || 0), 0);
          const totalSpent  = items.reduce((s, it) => s + getSpent(it), 0);
          const remaining   = totalBudget - totalSpent;
          const { nameSpans: baseNS, catSpans: baseCS } = getRowSpans(items);
          const extraRows = items.map(it => expandedSpending === it.id ? (it.spendings || []).length + 1 : 0);
          const nameSpans = [...baseNS]; const catSpans = [...baseCS];
          for (let i = 0; i < items.length; i++) {
            if (nameSpans[i] > 0) { let ex = 0; for (let j = i; j < i + baseNS[i]; j++) ex += extraRows[j]; nameSpans[i] += ex; }
            if (catSpans[i] > 0)  { let ex = 0; for (let j = i; j < i + baseCS[i]; j++) ex += extraRows[j]; catSpans[i]  += ex; }
          }
          const isCollapsed = collapsed[cat.id];
          return (
            <div key={cat.id} className={`rounded-xl overflow-hidden border ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
              {/* 카테고리 헤더 */}
              <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-center gap-2 flex-1">
                  <button onClick={() => setCollapsed(p => ({ ...p, [cat.id]: !p[cat.id] }))} className={isDark ? 'text-white/40 hover:text-white' : 'text-gray-400 hover:text-gray-700'}>
                    {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                  </button>
                  {isSuperAdmin ? (
                    <input defaultValue={cat.name} onBlur={e => updateCategoryName(cat.id, e.target.value)}
                      className={`bg-transparent font-semibold text-sm outline-none rounded px-2 py-1 min-w-0 ${isDark ? 'text-white focus:bg-white/5' : 'text-gray-900 focus:bg-gray-100'}`} />
                  ) : (
                    <span className={`font-semibold text-sm px-2 py-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{cat.name}</span>
                  )}
                </div>
                <div className={`flex items-center gap-4 text-xs mr-3 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                  <span>예산 <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{fmt(totalBudget)}</span></span>
                  <span>지출 <span className="text-amber-500 font-medium">{fmt(totalSpent)}</span></span>
                  <span>잔여 <span className={`font-medium ${remaining < 0 ? 'text-red-400' : isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{fmt(remaining)}</span></span>
                </div>
                {isSuperAdmin && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => addItem(cat.id)}
                      className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg transition-colors ${isDark ? 'bg-white/10 hover:bg-white/15 text-white/70' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}>
                      <Plus size={12} /> 행 추가
                    </button>
                    <button onClick={() => deleteCategory(cat.id)} className={`transition-colors p-1 ${isDark ? 'text-white/20 hover:text-red-400' : 'text-gray-300 hover:text-red-500'}`}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                )}
              </div>

              {!isCollapsed && (
                <div className="overflow-x-auto [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent">
                  <table className="budget-table text-xs border-collapse w-full">
                    <colgroup>
                      <col style={{ width: '48px' }} />   {/* drag/star/병합 */}
                      <col style={{ width: '72px' }} />   {/* 항목 */}
                      <col style={{ width: '72px' }} />   {/* 구분 */}
                      <col />                              {/* 내용 (flex) */}
                      <col style={{ width: '80px' }} />   {/* 단가 */}
                      <col style={{ width: '48px' }} />   {/* 수량 */}
                      <col style={{ width: '40px' }} />   {/* 회 */}
                      <col style={{ width: '80px' }} />   {/* 예산 */}
                      <col style={{ width: '88px' }} />   {/* 지출금액 */}
                      <col style={{ width: '72px' }} />   {/* 집행인 */}
                      <col style={{ width: '90px' }} />   {/* 지출일자 */}
                      <col style={{ width: '80px' }} />   {/* 지출방법 */}
                      <col style={{ width: '52px' }} />   {/* 영수증 */}
                      <col style={{ width: '90px' }} />   {/* 비고 */}
                      <col style={{ width: '28px' }} />   {/* 삭제 */}
                    </colgroup>
                    <thead>
                      <tr>
                        <th className={hCls}></th>
                        <th className={hCls}>항목</th>
                        <th className={hCls}>구분</th>
                        <th className={hCls}>내용</th>
                        <th className={hCls}>단가</th>
                        <th className={hCls}>수량</th>
                        <th className={hCls}>회</th>
                        <th className={hCls}>예산</th>
                        <th className={hCls}>지출금액</th>
                        <th className={hCls}>집행인</th>
                        <th className={hCls}>지출일자</th>
                        <th className={hCls}>지출방법</th>
                        <th className={hCls}>영수증</th>
                        <th className={hCls}>비고</th>
                        <th className={hCls}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr><td colSpan={15} className={`py-6 text-center ${isDark ? 'text-white/30' : 'text-gray-400'}`}>항목이 없습니다. "행 추가"를 눌러 추가하세요.</td></tr>
                      ) : items.map((it, idx) => {
                        const span = nameSpans[idx]; const catSpan = catSpans[idx];
                        const isDragging = dragItem?.catId === cat.id && dragItem?.itemId === it.id;
                        const isDragOver = dragOverItem?.catId === cat.id && dragOverItem?.itemId === it.id;
                        const sps = it.spendings || []; const hasSp = sps.length > 0;
                        const spSum = hasSp ? sps.reduce((s, sp) => s + (Number(sp.amount) || 0), 0) : 0;
                        const rowBg = it.isImportant
                          ? (isDark ? 'bg-red-500/10' : 'bg-red-50')
                          : getSpent(it) > 0
                            ? (isDark ? 'bg-yellow-500/10' : 'bg-yellow-50/50')
                            : (isDark ? 'hover:bg-white/3' : 'hover:bg-gray-50');
                        return (
                          <Fragment key={it.id}>
                          <tr draggable={isSuperAdmin}
                            onDragStart={isSuperAdmin ? () => onDragStart(cat.id, it.id) : undefined}
                            onDragOver={isSuperAdmin ? e => onDragOver(e, cat.id, it.id) : undefined}
                            onDrop={isSuperAdmin ? () => onDrop(cat.id, it.id) : undefined}
                            onDragEnd={isSuperAdmin ? onDragEnd : undefined}
                            className={`transition-colors ${rowBg} ${isDragging ? 'opacity-40' : ''} ${isDragOver ? 'border-t-2 border-brand' : ''}`}>
                            {/* drag + star */}
                            <td className={`${cellCls} text-center`}>
                              {isSuperAdmin ? (
                                <div className="flex items-center justify-center gap-0.5">
                                  <GripVertical size={11} className={`cursor-grab ${isDark ? 'text-white/15' : 'text-gray-300'}`} />
                                  <button onClick={() => { updateItemLocal(cat.id, it.id, 'isImportant', !it.isImportant); saveItem({ ...it, isImportant: !it.isImportant }); }}>
                                    <Star size={11} className={it.isImportant ? 'text-amber-400 fill-amber-400' : isDark ? 'text-white/15 hover:text-white/40' : 'text-gray-300 hover:text-gray-500'} />
                                  </button>
                                  {/* 위 행과 병합/분리 — 첫 행은 위가 없으므로 제외 */}
                                  {idx > 0 && (span === 0 ? (
                                    <button title="위 항목과 분리" onClick={() => toggleSplit(cat.id, it, true)}>
                                      <Unlink2 size={10} className={isDark ? 'text-white/20 hover:text-brand' : 'text-gray-300 hover:text-brand'} />
                                    </button>
                                  ) : (
                                    <button title="위 항목과 합치기" onClick={() => toggleSplit(cat.id, it, false)}>
                                      <Link2 size={10} className={isDark ? 'text-white/20 hover:text-brand' : 'text-gray-300 hover:text-brand'} />
                                    </button>
                                  ))}
                                </div>
                              ) : <Star size={11} className={it.isImportant ? 'text-amber-400 fill-amber-400 mx-auto' : isDark ? 'text-white/10 mx-auto' : 'text-gray-200 mx-auto'} />}
                            </td>
                            {/* 항목 */}
                            {span > 0 && (
                              <td className={`${cellCls} align-middle`} rowSpan={span}>
                                {isSuperAdmin
                                  ? <input value={it.itemName || ''} onChange={e => updateItemLocal(cat.id, it.id, 'itemName', e.target.value)} onBlur={() => saveItem(it)} className={inputCls} placeholder="항목명" />
                                  : <span className={`text-xs px-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{it.itemName}</span>}
                              </td>
                            )}
                            {/* 구분 */}
                            {catSpan > 0 && (
                              <td className={`${cellCls} align-middle`} rowSpan={catSpan}>
                                {isSuperAdmin
                                  ? <input value={it.itemCategory || ''} onChange={e => updateItemLocal(cat.id, it.id, 'itemCategory', e.target.value)} onBlur={() => saveItem(it)} className={inputCls} placeholder="구분" />
                                  : <span className={`text-xs px-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{it.itemCategory}</span>}
                              </td>
                            )}
                            {/* 내용 */}
                            <td className={cellCls}>
                              {isSuperAdmin
                                ? focusedCell === `${it.id}-desc`
                                  ? <input value={it.description || ''} onChange={e => updateItemLocal(cat.id, it.id, 'description', e.target.value)}
                                      onBlur={() => { setFocusedCell(null); saveItem(it); }} className={inputCls} autoFocus />
                                  : <div onClick={() => setFocusedCell(`${it.id}-desc`)} className={`truncate text-xs px-1 py-0.5 cursor-text rounded min-h-[18px] ${isDark ? 'text-white hover:bg-white/5' : 'text-gray-900 hover:bg-gray-100'}`} title={it.description || ''}>
                                      {it.description || <span className={isDark ? 'text-white/20' : 'text-gray-300'}>내용</span>}
                                    </div>
                                : <div className={`truncate text-xs px-1 py-0.5 min-h-[18px] ${isDark ? 'text-white' : 'text-gray-700'}`}>{it.description}</div>}
                            </td>
                            {/* 단가 */}
                            <td className={cellCls}>
                              {isSuperAdmin
                                ? <input type="text"
                                    value={focusedCell === `${it.id}-up` ? String(it.unitPrice || 0) : fmt(it.unitPrice)}
                                    onChange={e => updateItemLocal(cat.id, it.id, 'unitPrice', Number(e.target.value.replace(/,/g,'')) || 0)}
                                    onFocus={() => setFocusedCell(`${it.id}-up`)} onBlur={() => { setFocusedCell(null); saveItem(it); }}
                                    className={numInputCls} />
                                : <span className={`text-xs text-right block px-1 ${isDark ? 'text-white' : 'text-gray-700'}`}>{fmt(it.unitPrice)}</span>}
                            </td>
                            {/* 수량 */}
                            <td className={cellCls}>
                              {isSuperAdmin
                                ? <input type="number" min="1" value={it.quantity || 1} onChange={e => updateItemLocal(cat.id, it.id, 'quantity', Number(e.target.value) || 1)} onBlur={() => saveItem(it)} className={centerInputCls} />
                                : <span className={`text-xs text-center block px-1 ${isDark ? 'text-white' : 'text-gray-700'}`}>{it.quantity || 1}</span>}
                            </td>
                            {/* 회 */}
                            <td className={cellCls}>
                              {isSuperAdmin
                                ? <input type="number" min="1" value={it.unit || 1} onChange={e => updateItemLocal(cat.id, it.id, 'unit', Number(e.target.value) || 1)} onBlur={() => saveItem(it)} className={centerInputCls} />
                                : <span className={`text-xs text-center block px-1 ${isDark ? 'text-white' : 'text-gray-700'}`}>{it.unit || 1}</span>}
                            </td>
                            {/* 예산 */}
                            <td className={cellCls}>
                              {isSuperAdmin
                                ? <input type="text"
                                    value={focusedCell === `${it.id}-ba` ? String(it.budgetAmount || 0) : fmt(it.budgetAmount)}
                                    onChange={e => updateItemLocal(cat.id, it.id, 'budgetAmount', Number(e.target.value.replace(/,/g,'')) || 0)}
                                    onFocus={() => setFocusedCell(`${it.id}-ba`)} onBlur={() => { setFocusedCell(null); saveItem(it); }}
                                    className={numInputCls} />
                                : <span className={`text-xs text-right block px-1 ${isDark ? 'text-white' : 'text-gray-700'}`}>{fmt(it.budgetAmount)}</span>}
                            </td>
                            {/* 지출금액 */}
                            <td className={cellCls}>
                              <div className="flex items-center gap-0.5">
                                {hasSp ? (
                                  <span className="text-xs text-right flex-1 font-medium px-1 cursor-default"
                                    style={{ color: spSum > (it.budgetAmount||0) ? '#f87171' : spSum>0 && spSum<(it.budgetAmount||0) ? '#60a5fa' : isDark ? '#fff' : '#111' }}>
                                    {fmt(spSum)}
                                  </span>
                                ) : (
                                  <input type="text"
                                    value={focusedCell === `${it.id}-sa` ? String(it.spentAmount || 0) : fmt(it.spentAmount)}
                                    onChange={e => updateItemLocal(cat.id, it.id, 'spentAmount', Number(e.target.value.replace(/,/g,'')) || 0)}
                                    onFocus={() => setFocusedCell(`${it.id}-sa`)} onBlur={() => { setFocusedCell(null); saveItem(it); }}
                                    className={`${numInputCls} flex-1`}
                                    style={focusedCell !== `${it.id}-sa` ? {
                                      color: (it.spentAmount||0)>(it.budgetAmount||0) ? '#f87171'
                                           : (it.spentAmount||0)<(it.budgetAmount||0)&&(it.spentAmount||0)>0 ? '#60a5fa'
                                           : undefined
                                    } : {}} />
                                )}
                                <button onClick={() => setExpandedSpending(p => p === it.id ? null : it.id)}
                                  className={`shrink-0 transition-colors ${expandedSpending === it.id ? 'text-violet-400' : hasSp ? 'text-amber-400/60 hover:text-amber-400' : isDark ? 'text-white/15 hover:text-violet-400' : 'text-gray-200 hover:text-violet-500'}`}>
                                  <List size={12} />
                                </button>
                              </div>
                            </td>
                            {/* 집행인 */}
                            <td className={`${cellCls} text-center`}>
                              <select value={it.executor || ''} onChange={e => { updateItemLocal(cat.id, it.id, 'executor', e.target.value); saveItem({ ...it, executor: e.target.value }); }} className={centerSelectCls}>
                                <option value="" style={{ background: optBg }}>-</option>
                                {EXECUTORS.map(ex => <option key={ex} value={ex} style={{ background: optBg }}>{ex}</option>)}
                              </select>
                            </td>
                            {/* 지출일자 */}
                            <td className={`${cellCls} text-center`}>
                              <input type="date" onClick={e => e.currentTarget.showPicker?.()} value={it.spentDate || ''} onChange={e => updateItemLocal(cat.id, it.id, 'spentDate', e.target.value)} onBlur={() => saveItem(it)}
                                className={`w-full bg-transparent text-[11px] outline-none rounded px-0.5 py-0.5 text-center ${isDark ? 'text-white [color-scheme:dark]' : 'text-gray-900 [color-scheme:light]'}`} />
                            </td>
                            {/* 지출방법 */}
                            <td className={`${cellCls} text-center`}>
                              <select value={it.paymentMethod || ''} onChange={e => { updateItemLocal(cat.id, it.id, 'paymentMethod', e.target.value); saveItem({ ...it, paymentMethod: e.target.value }); }} className={centerSelectCls}>
                                <option value="" style={{ background: optBg }}>-</option>
                                {METHODS.map(m => <option key={m} value={m} style={{ background: optBg }}>{m}</option>)}
                              </select>
                            </td>
                            {/* 영수증 */}
                            <td className={`${cellCls} text-center`}>
                              <select value={it.receiptYn || '-'} onChange={e => { updateItemLocal(cat.id, it.id, 'receiptYn', e.target.value); saveItem({ ...it, receiptYn: e.target.value }); }}
                                className={centerSelectCls}>
                                {RECEIPTS.map(r => <option key={r} value={r} style={{ background: optBg }}>{r}</option>)}
                              </select>
                            </td>
                            {/* 비고 */}
                            <td className={cellCls}>
                              <input value={it.note || ''} onChange={e => updateItemLocal(cat.id, it.id, 'note', e.target.value)} onBlur={() => saveItem(it)} className={inputCls} placeholder="비고" />
                            </td>
                            {/* 삭제 */}
                            <td className={`${cellCls} text-center`}>
                              {isSuperAdmin && (
                                <button onClick={() => deleteItem(cat.id, it.id)} className={`transition-colors ${isDark ? 'text-white/20 hover:text-red-400' : 'text-gray-300 hover:text-red-500'}`}>
                                  <Trash2 size={13} />
                                </button>
                              )}
                            </td>
                          </tr>
                          {expandedSpending === it.id && (
                            <>
                              {sps.map(sp => (
                                <tr key={`sp-${sp.id}`} className={isDark ? 'bg-violet-500/5' : 'bg-violet-50/50'}>
                                  <td className={`${cellCls} text-center ${isDark ? 'text-violet-400/30' : 'text-violet-300'}`}><span className="text-[10px]">└</span></td>
                                  <td colSpan={5} className={cellCls}><input value={sp.description || ''} onChange={e => updateSpendingLocal(cat.id, it.id, sp.id, 'description', e.target.value)} onBlur={() => saveSpending(sp)} className={inputCls} placeholder="세부 내용" /></td>
                                  <td className={cellCls}>
                                    <input type="text" value={focusedCell === `sp-${sp.id}` ? String(sp.amount || 0) : fmt(sp.amount)}
                                      onChange={e => updateSpendingLocal(cat.id, it.id, sp.id, 'amount', Number(e.target.value.replace(/,/g,'')) || 0)}
                                      onFocus={() => setFocusedCell(`sp-${sp.id}`)} onBlur={() => { setFocusedCell(null); saveSpending(sp); }} className={numInputCls} />
                                  </td>
                                  <td className={`${cellCls} text-center`}><select value={sp.executor || ''} onChange={e => { updateSpendingLocal(cat.id, it.id, sp.id, 'executor', e.target.value); saveSpending({ ...sp, executor: e.target.value }); }} className={centerSelectCls}><option value="" style={{ background: optBg }}>-</option>{EXECUTORS.map(ex => <option key={ex} value={ex} style={{ background: optBg }}>{ex}</option>)}</select></td>
                                  <td className={`${cellCls} text-center`}><input type="date" onClick={e => e.currentTarget.showPicker?.()} value={sp.spentDate || ''} onChange={e => updateSpendingLocal(cat.id, it.id, sp.id, 'spentDate', e.target.value)} onBlur={() => saveSpending(sp)} className={`w-full bg-transparent text-[11px] outline-none rounded px-0.5 py-0.5 text-center ${isDark ? 'text-white [color-scheme:dark]' : 'text-gray-900 [color-scheme:light]'}`} /></td>
                                  <td className={`${cellCls} text-center`}><select value={sp.paymentMethod || ''} onChange={e => { updateSpendingLocal(cat.id, it.id, sp.id, 'paymentMethod', e.target.value); saveSpending({ ...sp, paymentMethod: e.target.value }); }} className={centerSelectCls}><option value="" style={{ background: optBg }}>-</option>{METHODS.map(m => <option key={m} value={m} style={{ background: optBg }}>{m}</option>)}</select></td>
                                  <td className={`${cellCls} text-center`}><select value={sp.receiptYn || '-'} onChange={e => { updateSpendingLocal(cat.id, it.id, sp.id, 'receiptYn', e.target.value); saveSpending({ ...sp, receiptYn: e.target.value }); }} className={centerSelectCls}>{RECEIPTS.map(r => <option key={r} value={r} style={{ background: optBg }}>{r}</option>)}</select></td>
                                  <td className={cellCls}><input value={sp.note || ''} onChange={e => updateSpendingLocal(cat.id, it.id, sp.id, 'note', e.target.value)} onBlur={() => saveSpending(sp)} className={inputCls} placeholder="비고" /></td>
                                  <td className={`${cellCls} text-center`}><button onClick={() => deleteSpending(cat.id, it.id, sp.id)} className={`transition-colors ${isDark ? 'text-white/20 hover:text-red-400' : 'text-gray-300 hover:text-red-500'}`}><Trash2 size={11} /></button></td>
                                </tr>
                              ))}
                              <tr className={isDark ? 'bg-violet-500/5' : 'bg-violet-50/50'}>
                                <td className={cellCls}></td>
                                <td colSpan={13} className={`${cellCls} text-left`}>
                                  <button onClick={() => addSpending(cat.id, it.id)}
                                    className={`flex items-center gap-1 text-[11px] transition-colors ${isDark ? 'text-white/30 hover:text-violet-400' : 'text-gray-400 hover:text-violet-500'}`}>
                                    <Plus size={11} /> 세부지출 추가
                                  </button>
                                </td>
                              </tr>
                            </>
                          )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                    {items.length > 0 && (
                      <tfoot>
                        <tr className={`font-medium ${isDark ? 'bg-white/5 text-white/60' : 'bg-gray-50 text-gray-500'}`}>
                          <td className={`${cellCls} text-right`} colSpan={7}>합계</td>
                          <td className={`${cellCls} text-right ${isDark ? 'text-white' : 'text-gray-900'}`}>{fmt(totalBudget)}</td>
                          <td className={`${cellCls} text-right text-amber-500`}>{fmt(totalSpent)}</td>
                          <td colSpan={6} className={`${cellCls} text-right ${remaining < 0 ? 'text-red-400' : isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>잔여 {fmt(remaining)}원</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </div>
          );
        })}

        {/* 대분류 추가 */}
        {isSuperAdmin && (showNewCat ? (
          <div className="flex items-center gap-2">
            <input value={newCatName} onChange={e => setNewCatName(e.target.value)} onKeyDown={e => e.key === 'Enter' && addCategory()}
              className={`flex-1 border rounded-lg px-3 py-2 text-sm outline-none transition-colors ${isDark ? 'bg-white/5 border-white/10 text-white focus:border-brand' : 'bg-white border-gray-200 text-gray-900 focus:border-brand'}`}
              placeholder="대분류 이름 (예: 무대/음향, 인력비, 홍보비)" autoFocus />
            <button onClick={addCategory} className="px-4 py-2 bg-brand hover:bg-brand/90 text-white text-sm rounded-lg">추가</button>
            <button onClick={() => { setShowNewCat(false); setNewCatName(''); }} className={`px-4 py-2 text-sm rounded-lg ${isDark ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-500'}`}>취소</button>
          </div>
        ) : (
          <button onClick={() => setShowNewCat(true)}
            className={`flex items-center gap-2 px-5 py-3 border border-dashed rounded-xl text-sm transition-colors w-full justify-center ${isDark ? 'border-white/20 text-white/40 hover:border-brand/50 hover:text-brand' : 'border-gray-300 text-gray-400 hover:border-brand/50 hover:text-brand'}`}>
            <Plus size={16} /> 대분류 추가
          </button>
        ))}
      </div>
    </div>
  );
}
